import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";

const area = "w-full border-2 border-[#D3CFC0] bg-white p-3 text-sm focus:border-[#1E1E1E] outline-none";
const lab = "block font-head uppercase text-sm text-[#1E1E1E] mb-2";

function Choice({ testid, value, options, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([v, l]) => (
        <button type="button" key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
          className={`px-4 py-2 border-2 text-sm font-head uppercase transition-colors ${value === v ? "bg-[#1E1E1E] border-[#1E1E1E] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#1E1E1E]"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function StarterKitFeedback() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ rating: 0, used_at_practice: "not_yet", most_useful: "", confusing: "", missing: "", would_pay: "maybe", comments: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/starter-kit/feedback/${token}`).then((r) => setInfo(r.data)).catch(() => setErr("This feedback link isn't valid.")); }, [token]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { setErr("Pick a rating first."); return; }
    setBusy(true); setErr("");
    try { await api.post(`/starter-kit/feedback/${token}`, f); setDone(true); }
    catch { setErr("Couldn't send — please try again."); } finally { setBusy(false); }
  };

  return (
    <main className="max-w-2xl mx-auto px-5 py-14" data-testid="sk-feedback-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[#DB9130] font-[IBM_Plex_Mono]">Starter Kit · Beta</p>
      <h1 className="font-head uppercase text-4xl sm:text-5xl text-[#1E1E1E] mt-2">How did it go, Coach?</h1>
      {info && <p className="text-[#6E6E6E] mt-3" data-testid="sk-feedback-combo">{info.sport} · Ages {info.age} · {info.practice_length} min{info.team_name ? ` · ${info.team_name}` : ""}</p>}
      {err && <p className="text-[#B3261E] mt-4" data-testid="sk-feedback-error">{err}</p>}
      {done ? (
        <div className="mt-10 border-2 border-[#1E1E1E] p-6 flex items-start gap-3" data-testid="sk-feedback-done">
          <Check className="text-[#DB9130] shrink-0" />
          <p className="text-[#1E1E1E]">Thank you — Coach Kyto reads every one of these. Your answers shape the final kit.</p>
        </div>
      ) : info && (
        <form onSubmit={submit} className="mt-10 space-y-8">
          <div>
            <label className={lab}>Overall, how useful was the kit?</label>
            <Choice testid="sk-fb-rating" value={String(f.rating)} onChange={(v) => setF((p) => ({ ...p, rating: +v }))} options={[1, 2, 3, 4, 5].map((n) => [String(n), String(n)])} />
          </div>
          <div><label className={lab}>Did you use it at a practice yet?</label>
            <Choice testid="sk-fb-used" value={f.used_at_practice} onChange={set("used_at_practice")} options={[["yes", "Yes"], ["not_yet", "Not yet"]]} /></div>
          <div><label className={lab}>What was most useful?</label><textarea data-testid="sk-fb-useful" rows={3} className={area} value={f.most_useful} onChange={set("most_useful")} /></div>
          <div><label className={lab}>Anything confusing or wrong?</label><textarea data-testid="sk-fb-confusing" rows={3} className={area} value={f.confusing} onChange={set("confusing")} /></div>
          <div><label className={lab}>What was missing?</label><textarea data-testid="sk-fb-missing" rows={3} className={area} value={f.missing} onChange={set("missing")} /></div>
          <div><label className={lab}>Would you pay $9 for this?</label>
            <Choice testid="sk-fb-pay" value={f.would_pay} onChange={set("would_pay")} options={[["yes", "Yes"], ["maybe", "Maybe"], ["no", "No"]]} /></div>
          <div><label className={lab}>Anything else?</label><textarea data-testid="sk-fb-comments" rows={3} className={area} value={f.comments} onChange={set("comments")} /></div>
          <button data-testid="sk-fb-submit" disabled={busy} className="flex items-center gap-2 px-6 py-3 bg-[#1E1E1E] text-[#F3F1EC] font-head uppercase hover:bg-[#DB9130] hover:text-[#1E1E1E] transition-colors disabled:opacity-50">
            {busy && <Loader2 size={16} className="animate-spin" />} Send feedback
          </button>
        </form>
      )}
    </main>
  );
}
